import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'

import { v4 as uniqid } from 'uuid'

import { DateTime } from 'luxon'

import SubmitForm  from '@/Form/V2/SubmitForm'
import ModelPicker from '@/Form/V2/ModelPicker'
import DatePicker  from '@/Form/V2/DatePicker'
import Checkbox    from '@/Form/V2/PullState/Checkbox'
import Select      from '@/Form/V2/Select'
import Radio       from '@/Form/V2/Radio'
import Input       from '@/Form/V2/Input'

import CreditNotesToAffect from '@/CreditNotesToAffect'
import getFormStatus       from '@/Form/Functions/getFormStatus'
import PickableBills       from '@/PickableBills'

import Loader from '@Pure/Loader'

import removeStatusMsg from '@function/removeStatusMsg'
import request         from '@function/ajax/request'

import { isTrue } from '@root/utility'

import FormsStore from '@Store/Forms'

const FORM_NAME = 'UserPaymentForm';

const payment_types =
[
	{ value: 'bank_transfer', label: 'Virement' },
	{ value: 'check', label: 'Chèque' },
	{ value: 'holidays_coupon', label: 'Chèque vacances' },
	{ value: 'credit_note', label: 'Avoir' },
	{ value: 'credit_card', label: 'CB' }
];

const FORM_INIT =
{
	type: '',
	date: DateTime.now().toISODate()
};

const pickers_infos =
{
	EC:
	{
		name: 'EC_id',
		label: 'Nom du CSE',
		model: 'enterprisecomitee'
	},

	partner:
	{
		name: 'partner_id',
		label: 'Nom du partenaire',
		model: 'partner'
	},

	member:
	{
		name: 'user_id',
		label: 'Nom du membre',
		model: 'user'
	},

	credit_note:
	{
		name: 'credit_note',
		label: 'Avoir',
		model: 'credit_note'
	}
};

export default function NewPaymentForm ({ ...props })
{
	const [submit, setSubmit] = useState();
	const [submitting, setSubmitting] = useState(false);
	const [status, setStatus] = useState({ });

	const [bills, setBills] = useState([]);
	const [to_affect, setToAffect] = useState([]);
	const [fetching_bills, setFetchingBills] = useState(false);

	const type = FormsStore.useState(s => s[FORM_NAME]?.type);

	const payment_type   = FormsStore.useState(s => s[FORM_NAME]?.payment_type || '');

	const picked_partner = FormsStore.useState(s => s[FORM_NAME]?.partner_id || '');
	const picked_CN      = FormsStore.useState(s => s[FORM_NAME]?.credit_note || '');
	const picked_user    = FormsStore.useState(s => s[FORM_NAME]?.user_id || '');
	const picked_EC      = FormsStore.useState(s => s[FORM_NAME]?.EC_id || '');

	const confirm_credit_note = FormsStore
	.useState(s => isTrue(s[FORM_NAME]?.confirm_credit_note));

	useEffect(function ()
	{
		FormsStore.update(function (store)
		{
			store[FORM_NAME] = { ...FORM_INIT };
		});
	},
	[]);

	useEffect(function ()
	{
		if (!confirm_credit_note) return () => void 0;

		setStatus(function (status)
		{
			return { ...status, error: undefined }
		});
	},
	[confirm_credit_note]);

	useEffect(function ()
	{
		if (payment_type === 'credit_note')
		{
			FormsStore.update(function (store)
			{
				store[FORM_NAME].amount = '0';
			});
		}
	},
	[payment_type]);

	const setType = useCallback(function (e)
	{
		const type = e?.target?.value;

		if (!type) return;

		FormsStore.update(function (store)
		{
			store[FORM_NAME] ||= { };

			store[FORM_NAME].type = type;

			if (type !== 'member') store[FORM_NAME].user_id = '';
			if (type !== 'EC') store[FORM_NAME].EC_id = '';
			if (type !== 'partner') store[FORM_NAME].partner_id = '';
		});
	},
	[]);

	const removeFieldError = useCallback(function (name, params)
	{
		setStatus(status => removeStatusMsg(status, name));
	},
	[]);

	const picked = picked_user || picked_EC || picked_partner || picked_CN;

	useEffect(function ()
	{
		if (!picked || !type) return;

		const controller = new AbortController();

		(async function ()
		{
			setFetchingBills(true);

			try
			{
				var response = await request(
					'/get-selectable-bills-for-user-payment'
				)
				.config({ controller, keyword: 'action', min_req_time: 300 })
				.post(
				{
					type: type, picked_id: picked
				});
			}
			catch (update)
			{
				if (controller?.signal?.aborted) return;

				setFetchingBills(false);

				return setStatus(update);
			}

			setFetchingBills(false);
			setBills(response?.data || []);
			setToAffect(response?.to_affect || []);

			FormsStore.update(function (store)
			{
				Object.keys(store[FORM_NAME] || { })
				.filter(key => key.startsWith('bills[') || key.startsWith('amounts['))
				.forEach(key => delete store[FORM_NAME][key]);
			});
		})();

		return () => controller?.abort?.();
	},
	[type, picked]);

	const sendForm = useCallback(function (e)
	{
		if (submitting) return;

		FormsStore.update(function (store)
		{
			store[FORM_NAME]._submit_id = uniqid();
		});

		setSubmitting(true);
		setSubmit(uniqid());
	},
	[submitting]);

	const onError = useCallback(function (update)
	{
		setSubmit(undefined);
		setStatus(update);
		setSubmitting(false);
	},
	[]);

	const onSuccess = useCallback(function (update)
	{
		setSubmit(undefined);
		setStatus(update);
		setSubmitting(false);

		FormsStore.update(function (store)
		{
			store[FORM_NAME] = { ...FORM_INIT };
		});
	},
	[]);

	const payment_fields = picked ?
	(
		<div className="c-form__payment-fields">
			<DatePicker name="date" label="Date du règlement"
				form={ FORM_NAME }
				bemit={ ['25%'] }
				error={ status.fields?.['date']?.error }
			/>

			<Select name="payment_type"
				label="Moyen de règlement"
				options={ payment_types }
				form={ FORM_NAME }
				error={ status.fields?.['payment_type']?.error }
				removeError={ removeFieldError }
				bemit={ ['25%'] }
			/>

			{
				['check', 'holidays_coupon', 'credit_card'].includes(payment_type) ?
				(
					<Input name="number"
						label={ payment_type === 'credit_card'
							? 'N° de transaction' : 'N° de chèque' }
						form={ FORM_NAME }
						bemit={ ['25%'] }
						error={ status.fields?.['number']?.error }
					/>
				)
				: undefined
			}

			<Input name="amount" label="Montant du règlement (€)"
				form={ FORM_NAME }
				bemit={ ['25%'] }
				error={ status.fields?.['amount']?.error }
				safe-number
				read-only={ payment_type === 'credit_note' }
			/>
		</div>
	)
	: undefined;

	const pick_bills_area = payment_fields ?
	(
		<div className="u-full-width u-positioned" style={ { minHeight: 200 } }>

			{
				type !== 'credit_note' && payment_type === 'credit_note' ?
				(
					<CreditNotesToAffect form={ FORM_NAME } list={ to_affect }
						status={ status }
					/>
				)
				: undefined
			}

			<PickableBills form={ FORM_NAME } bills={ bills }
				status={ status } type={ type }
			/>

			{
				status.fields?.bills?.error
				? getFormStatus({ error: status.fields.bills.error })
				: undefined
			}

			{
				fetching_bills ?
				(
					<Loader className="c-loader" bemit={ ['bg-white', 'primary'] }
						text="Chargement des factures"
					/>
				)
				: undefined
			}
		</div>
	)
	: undefined;

	const picker_error = status.fields?.[name]?.error;

	const picker = useMemo(function ()
	{
		if (!type) return null;

		const name = pickers_infos[type]?.name || '';

		return (
			<ModelPicker key={ type }
				name={ name }
				label={ pickers_infos[type]?.label }
				model={ pickers_infos[type]?.model }
				error={ picker_error }
				bemit={ ['50%'] }
				form={ FORM_NAME }
				url="/autocomplete/user-payment-propositions"
				custom={ { type: type } }
			/>
		);
	},
	[type, picker_error]);

	return (
		<>

			<div className="c-form c-form--new-payment">

				<h2 className="c-title c-title--form">
					Règlement
				</h2>

				<div className="c-form__content-fields">

					<div className="u-full-width u-flex u-flex-wrap"
						style={ { gap: 30 } }>

						<Radio bemit={ ['new-payment-type'] }
							label="Facture à un CSE"
							name="type"
							value="EC"
							checked={ type === 'EC' }
							onChange={ setType }
						/>

						<Radio bemit={ ['new-payment-type'] }
							label="Facture à un membre ou élu"
							name="type"
							value="member"
							checked={ type === 'member' }
							onChange={ setType }
						/>

						<Radio bemit={ ['new-payment-type'] }
							label="Facture à un partenaire"
							name="type"
							value="partner"
							checked={ type === 'partner' }
							onChange={ setType }
						/>

						<Radio bemit={ ['new-payment-type'] }
							label="Avoir"
							name="type"
							value="credit_note"
							checked={ type === 'credit_note' }
							onChange={ setType }
						/>

					</div>

					{ picker }

					{ payment_fields }

					{ pick_bills_area }

				</div>

				{ getFormStatus(status) }

				{
					status.ERROR_CODE === 'needs_confirmation' ?
					(
						<Checkbox label={ 'Créer un avoir' +
							(
								status.fields.__amount
								? ' de ' + status.fields.__amount + '€'
								: ''
						) }
							name="confirm_credit_note"
							bemit={ ['50%'] }
							form={ FORM_NAME }
						/>
					)
					: undefined
				}

			</div>

			{
				submit ?
				(
					<SubmitForm form={ FORM_NAME } id={ submit }
						onError={ onError }
						onSuccess={ onSuccess }
						url="/user-payments/create"
					/>
				)
				: undefined
			}

			<button type="button" className="c-btn c-btn--extra c-btn--green
				u-margin-top-30"
				onClick={ sendForm }>
				Valider ce règlement
				{
					submitting ?
					(
						<Loader bemit={ ['bg-green', 'white'] }
							className="c-loader"
						/>
					)
					: undefined
				}
			</button>

		</>
	);
}